import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ThemeContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default theme (Coastal Silk - Light)
const DEFAULT_THEME = {
  preset: 'coastalSilk',
  mode: 'light',
  colors: {
    background: '#F6F4EF',
    surface: '#FFFFFF',
    primary: '#A7B7A8',
    secondary: '#AFC2D3',
    text: '#1e293b',
    textMuted: '#CFCFCB'
  },
  isCustom: false
};

// Default admin workspace colors
const DEFAULT_WORKSPACE_COLORS = {
  dark: {
    accent: '#D4AF37',
    accentHover: '#B5952F',
  },
  light: {
    // Colors
    background: '#F6F4EF',
    card: '#ffffff',
    text: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    // Accent
    accent: '#A7B7A8',
    accentHover: '#8FA090',
  }
};

// Get cached theme from localStorage or use default
const getCachedTheme = () => {
  try {
    const cached = localStorage.getItem('app_theme');
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('Failed to parse cached theme:', e);
  }
  return DEFAULT_THEME;
};

// Get cached workspace colors from localStorage or use default
const getCachedWorkspaceColors = () => {
  try {
    const cached = localStorage.getItem('workspace_colors');
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('Failed to parse cached workspace colors:', e);
  }
  return DEFAULT_WORKSPACE_COLORS;
};

// Admin theme classes for dark/light mode - light mode accepts custom colors
const getAdminTheme = (isDarkMode, customColors = null) => {
  // Dark mode always uses default gold colors - NOT customizable
  const darkAccent = '#D4AF37';
  const darkAccentHover = '#B5952F';
  
  // Light mode uses custom colors if set
  const colors = customColors || DEFAULT_WORKSPACE_COLORS;
  const light = colors.light || {};
  const lightBg = light.background || '#F6F4EF';
  const lightCard = light.card || '#ffffff';
  const lightText = light.text || '#1e293b';
  const lightTextSecondary = light.textSecondary || '#64748b';
  const lightBorder = light.border || '#e2e8f0';
  const lightAccent = light.accent || '#A7B7A8';
  const lightAccentHover = light.accentHover || '#8FA090';
  
  return isDarkMode ? {
    // Dark mode - FIXED, not customizable
    bg: 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950',
    bgSolid: 'bg-slate-950',
    bgCard: 'bg-slate-900',
    bgCardHover: 'hover:bg-slate-800',
    bgInput: 'bg-slate-800',
    text: 'text-white',
    textSecondary: 'text-slate-400',
    textMuted: 'text-slate-500',
    border: 'border-slate-700',
    borderColor: 'border-slate-800',
    accent: 'bg-[#D4AF37]',
    accentText: 'text-[#D4AF37]',
    accentHover: 'hover:bg-[#B5952F]',
    buttonPrimary: 'bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]',
    buttonSecondary: 'bg-slate-800 text-white hover:bg-slate-700',
    buttonOutline: 'border-slate-700 text-slate-300 hover:bg-slate-800',
    dialogBg: 'bg-slate-900 border-slate-700',
    inputBg: 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500',
    tableBg: 'bg-slate-900',
    tableHeader: 'text-slate-400',
    tableRow: 'border-slate-800',
    tableRowHover: 'hover:bg-slate-800/50',
    tabActive: 'data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950',
    shadow: 'shadow-xl shadow-black/20',
    selectBg: 'bg-slate-800',
    badge: 'bg-slate-800 text-slate-300',
    badgeSuccess: 'bg-green-900/50 text-green-400',
    badgeWarning: 'bg-yellow-900/50 text-yellow-400',
    badgeError: 'bg-red-900/50 text-red-400',
    colors: {
      accent: darkAccent,
      accentHover: darkAccentHover,
    },
    useInlineStyles: false, // Never use inline styles for dark mode
  } : {
    // Light mode - CUSTOMIZABLE (solid colors, no gradient)
    bg: '', // Use inline style
    bgSolid: '', // Use inline style
    bgCard: '', // Use inline style
    bgCardHover: 'hover:opacity-95',
    bgInput: '', // Use inline style
    text: '', // Use inline style
    textSecondary: '', // Use inline style
    textMuted: '', // Use inline style  
    border: '', // Use inline style
    borderColor: '', // Use inline style
    accent: '',
    accentText: '',
    accentHover: '',
    buttonPrimary: 'text-white',
    buttonSecondary: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
    buttonOutline: '',
    dialogBg: '',
    inputBg: '',
    tableBg: '',
    tableHeader: '',
    tableRow: '',
    tableRowHover: 'hover:opacity-95',
    tabActive: 'data-[state=active]:text-white',
    shadow: 'shadow-lg shadow-slate-200/50',
    selectBg: '',
    badge: 'bg-slate-100 text-slate-600',
    badgeSuccess: 'bg-green-100 text-green-700',
    badgeWarning: 'bg-yellow-100 text-yellow-700',
    badgeError: 'bg-red-100 text-red-700',
    // All light mode colors for inline styles
    colors: {
      background: lightBg,
      card: lightCard,
      text: lightText,
      textSecondary: lightTextSecondary,
      border: lightBorder,
      accent: lightAccent,
      accentHover: lightAccentHover,
    },
    useInlineStyles: true, // Always use inline styles for light mode
  };
};

export const ThemeProvider = ({ children }) => {
  // Initialize with cached theme to prevent flash
  const [theme, setTheme] = useState(getCachedTheme);
  const [loading, setLoading] = useState(true);
  
  // Admin dark/light mode
  const [adminDarkMode, setAdminDarkMode] = useState(() => {
    const saved = localStorage.getItem('adminDarkMode');
    return saved ? saved === 'true' : true; // Default to dark
  });
  
  // Workspace custom colors
  const [workspaceColors, setWorkspaceColors] = useState(getCachedWorkspaceColors);

  useEffect(() => {
    fetchTheme();
    fetchWorkspaceColors();
  }, []);
  
  // Save admin mode preference
  useEffect(() => {
    localStorage.setItem('adminDarkMode', adminDarkMode.toString());
    
    // Apply admin-light class to document body for CSS variable overrides
    if (adminDarkMode) {
      document.body.classList.remove('admin-light');
    } else {
      document.body.classList.add('admin-light');
    }
  }, [adminDarkMode]);

  // Dynamically update CSS variables based on workspace colors
  useEffect(() => {
    const root = document.documentElement;
    
    if (!adminDarkMode && workspaceColors?.light) {
      // Light mode - apply custom colors from database
      const light = workspaceColors.light;
      root.style.setProperty('--admin-bg', light.background || '#F6F4EF');
      root.style.setProperty('--admin-card', light.card || '#ffffff');
      root.style.setProperty('--admin-card-hover', light.cardHover || '#f8fafc');
      root.style.setProperty('--admin-border', light.border || '#e2e8f0');
      root.style.setProperty('--admin-text', light.text || '#1e293b');
      root.style.setProperty('--admin-text-secondary', light.textSecondary || '#64748b');
      root.style.setProperty('--admin-accent', light.accent || '#8B7355');
      root.style.setProperty('--admin-accent-hover', light.accentHover || '#725F47');
      root.style.setProperty('--admin-icon', light.accent || '#8B7355');
    } else {
      // Dark mode - use default dark colors
      root.style.setProperty('--admin-bg', '#020617');
      root.style.setProperty('--admin-card', '#0f172a');
      root.style.setProperty('--admin-card-hover', '#1e293b');
      root.style.setProperty('--admin-border', '#334155');
      root.style.setProperty('--admin-text', '#ffffff');
      root.style.setProperty('--admin-text-secondary', '#94a3b8');
      root.style.setProperty('--admin-accent', '#D4AF37');
      root.style.setProperty('--admin-accent-hover', '#B5952F');
      root.style.setProperty('--admin-icon', '#D4AF37');
    }
  }, [adminDarkMode, workspaceColors]);

  const fetchTheme = async () => {
    try {
      const response = await axios.get(`${API}/theme`);
      setTheme(response.data);
      localStorage.setItem('app_theme', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to fetch theme:', error);
      setTheme(getCachedTheme());
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkspaceColors = async () => {
    try {
      const response = await axios.get(`${API}/dashboard-theme`);
      if (response.data) {
        setWorkspaceColors(response.data);
        localStorage.setItem('workspace_colors', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Failed to fetch workspace colors:', error);
      // Use cached or default only if server is unreachable
      setWorkspaceColors(getCachedWorkspaceColors());
    }
  };

  // Refresh workspace colors periodically to sync across all users
  useEffect(() => {
    const interval = setInterval(() => {
      fetchWorkspaceColors();
    }, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const updateTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('app_theme', JSON.stringify(newTheme));
  };

  const refreshTheme = () => {
    fetchTheme();
  };
  
  const updateWorkspaceColors = (newColors) => {
    setWorkspaceColors(newColors);
    localStorage.setItem('workspace_colors', JSON.stringify(newColors));
  };
  
  const refreshWorkspaceColors = () => {
    fetchWorkspaceColors();
  };
  
  // Toggle admin dark/light mode
  const toggleAdminDarkMode = () => {
    setAdminDarkMode(prev => !prev);
  };
  
  // Get admin theme classes - now uses custom workspace colors
  const adminTheme = getAdminTheme(adminDarkMode, workspaceColors);

  // Helper to get CSS styles for themed pages (Login, Register only)
  const getThemedStyles = () => {
    const colors = theme.colors;
    return {
      background: colors.background,
      surface: colors.surface,
      primary: colors.primary,
      secondary: colors.secondary,
      text: colors.text,
      textMuted: colors.textMuted,
      mode: theme.mode
    };
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      updateTheme, 
      refreshTheme, 
      loading, 
      getThemedStyles,
      // Admin dark/light mode
      adminDarkMode,
      toggleAdminDarkMode,
      adminTheme,
      // Workspace customization
      workspaceColors,
      updateWorkspaceColors,
      refreshWorkspaceColors,
      defaultWorkspaceColors: DEFAULT_WORKSPACE_COLORS,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
